import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./KycReview.css";

const CONFIG = {
  pan: {
    title: "PAN KYC",
    label: "PAN",
    numberLabel: "PAN number",
    number: (d) => d?.panNumber,
    docs: (d) => [{ label: "PAN card", url: d?.document, field: "document" }],
  },
  aadhar: {
    title: "Aadhaar KYC",
    label: "Aadhaar",
    numberLabel: "Aadhaar number",
    number: (d) => (d?.aadharNumber ? String(d.aadharNumber).replace(/(\d{4})(?=\d)/g, "$1 ") : ""),
    docs: (d) => [
      { label: "Front", url: d?.documentFront, field: "documentFront" },
      { label: "Back", url: d?.documentBack, field: "documentBack" },
    ],
  },
};

const TABS = [
  { key: "pending", title: "Pending" },
  { key: "approved", title: "Approved" },
  { key: "rejected", title: "Rejected" },
];
const PAGE_SIZES = [20, 50, 100];
const EMPTY = { search: "", from: "", to: "" };
const initials = (r) => String(r.name || r.username || "?").trim().slice(0, 2).toUpperCase();
const when = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "—");

const formFrom = (d) => ({
  panNumber: d?.panNumber || "",
  aadharNumber: d?.aadharNumber || "",
  fatherName: d?.fatherName || "",
  relation: d?.relation === "husband" ? "husband" : "father",
  address: d?.address || "",
  pincode: d?.pincode || "",
});

/** Review popup: member, details, documents; approve / reject a pending request; edit details. */
const ReviewModal = ({ type, row, onClose, onDone, onSaved }) => {
  const cfg = CONFIG[type];
  const [mode, setMode] = useState("view"); // view | reject | edit
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState(row?.details || {});
  const [form, setForm] = useState(formFrom(row?.details));
  const [newFiles, setNewFiles] = useState({}); // field -> { file, url }

  useEffect(() => {
    setMode("view");
    setReason("");
    setDetails(row?.details || {});
    setForm(formFrom(row?.details));
    setNewFiles({});
  }, [row]);

  useEffect(() => () => Object.values(newFiles).forEach((f) => URL.revokeObjectURL(f.url)), [newFiles]);

  if (!row) return null;
  const d = details;
  const pending = row.status === 3;
  const editing = mode === "edit";
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickFile = (field) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) return toast.error("Choose a JPG, PNG or WEBP image.");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be 10 MB or smaller.");
    setNewFiles((prev) => ({ ...prev, [field]: { file, url: URL.createObjectURL(file) } }));
  };

  const save = async () => {
    const fd = new FormData();
    fd.append("uid", row.uid);
    fd.append("type", type);
    if (type === "pan") fd.append("panNumber", form.panNumber.trim().toUpperCase());
    else {
      fd.append("aadharNumber", form.aadharNumber.replace(/\D/g, ""));
      fd.append("fatherName", form.fatherName);
      fd.append("relation", form.relation);
      fd.append("address", form.address);
      fd.append("pincode", form.pincode);
    }
    Object.entries(newFiles).forEach(([field, f]) => fd.append(field, f.file));
    setSaving(true);
    const { data, error } = await apiCall("POST", "/kyc-review/update", fd);
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save.");
    toast.success(data?.message || "Details updated.");
    setDetails(data?.data || d);
    setForm(formFrom(data?.data || d));
    setNewFiles({});
    setMode("view");
    onSaved && onSaved();
  };

  const decide = async (action) => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/kyc-review/decide", { uid: row.uid, type, action, reason });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not update.");
    toast.success(data?.message || "Updated.");
    window.dispatchEvent(new Event("kyc-requests-changed"));
    onDone();
  };

  return (
    <Modal show onHide={onClose} centered size="lg" className="plan-settings-modal kyc-review-modal">
      <Modal.Header closeButton>
        <Modal.Title>{cfg.title}: {row.username}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ar-member-card">
          <span className="ar-avatar">{initials(row)}</span>
          <div>
            <div className="ps-strong">{row.name || row.username}</div>
            <div className="ps-muted ps-small">
              {row.username}
              {row.email ? ` · ${row.email}` : ""}
              {row.mobile ? ` · ${row.mobile}` : ""}
            </div>
          </div>
          <div className="ar-member-amount">
            <span className={`kr-status kr-status--${row.status}`}>{row.status === 3 ? "Pending" : row.status === 1 ? "Approved" : "Rejected"}</span>
          </div>
        </div>

        {editing ? (
          <div className="kr-edit-form">
            {type === "pan" ? (
              <Form.Group>
                <Form.Label>PAN number</Form.Label>
                <Form.Control
                  className="kr-number"
                  value={form.panNumber}
                  maxLength={10}
                  onChange={(e) => setForm((f) => ({ ...f, panNumber: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))}
                />
              </Form.Group>
            ) : (
              <>
                <Form.Group>
                  <Form.Label>Aadhaar number</Form.Label>
                  <Form.Control
                    className="kr-number"
                    value={form.aadharNumber}
                    inputMode="numeric"
                    maxLength={14}
                    onChange={(e) => setForm((f) => ({ ...f, aadharNumber: e.target.value.replace(/[^\d ]/g, "") }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Name of</Form.Label>
                  <div className="kr-relation">
                    <Form.Select value={form.relation} onChange={setF("relation")}>
                      <option value="father">Father</option>
                      <option value="husband">Husband</option>
                    </Form.Select>
                    <Form.Control value={form.fatherName} onChange={setF("fatherName")} placeholder={form.relation === "husband" ? "Husband's name" : "Father's name"} />
                  </div>
                </Form.Group>
                <Form.Group className="kr-wide">
                  <Form.Label>Address</Form.Label>
                  <Form.Control as="textarea" rows={2} value={form.address} onChange={setF("address")} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Pin code</Form.Label>
                  <Form.Control value={form.pincode} inputMode="numeric" maxLength={6} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "") }))} />
                </Form.Group>
              </>
            )}
            <p className="kr-edit-note kr-wide">
              Saving changes the details only; the request stays {row.status === 3 ? "pending" : row.status === 1 ? "approved" : "rejected"}.
            </p>
          </div>
        ) : (
        <div className="kr-facts">
          <div><span>{cfg.numberLabel}</span><strong className="kr-number">{cfg.number(d) || "—"}</strong></div>
          {type === "aadhar" && (
            <>
              <div><span>{d.relation === "husband" ? "Husband's name" : "Father's name"}</span><strong>{d.fatherName || "—"}</strong></div>
              <div className="kr-wide"><span>Address</span><strong>{d.address || "—"}{d.pincode ? ` · ${d.pincode}` : ""}</strong></div>
            </>
          )}
          <div><span>Submitted</span><strong>{when(row.submittedAt)}</strong></div>
          {!pending && (
            <div>
              <span>{row.status === 1 ? "Approved" : "Rejected"}</span>
              <strong>{when(d.reviewedAt)}{d.reviewedBy?.username ? ` by ${d.reviewedBy.username}` : ""}</strong>
            </div>
          )}
          {row.status === 2 && d.remarks && <div className="kr-wide"><span>Reason</span><strong>{d.remarks}</strong></div>}
        </div>
        )}

        <div className="kr-docs">
          {cfg.docs(d).map((doc) => {
            const replaced = newFiles[doc.field];
            const src = replaced?.url || doc.url;
            return (
              <figure key={doc.label}>
                {src ? (
                  <a href={src} target="_blank" rel="noopener noreferrer" title="Open full size">
                    <img src={src} alt={`${cfg.label} ${doc.label}`} />
                  </a>
                ) : (
                  <div className="kr-nodoc">No image</div>
                )}
                <figcaption>
                  {doc.label}
                  {replaced ? " · new image (saved when you click Save)" : src ? " · click to open full size" : ""}
                </figcaption>
                {editing && (
                  <label className="kr-replace">
                    {replaced ? "Choose another image" : "Replace image"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickFile(doc.field)} hidden />
                  </label>
                )}
              </figure>
            );
          })}
        </div>

        {mode === "reject" && (
          <Form.Group className="mt-3">
            <Form.Label>Reason (shown to the member)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`e.g. ${cfg.label} image is blurred, number does not match`}
              autoFocus
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer className="kr-footer">
        {editing ? (
          <span />
        ) : (
          <Button variant="link" className="kr-edit" onClick={() => setMode("edit")} disabled={saving}>
            Edit details
          </Button>
        )}
        <div className="kr-footer-actions">
          {editing ? (
            <>
              <Button variant="outline-secondary" onClick={() => { setMode("view"); setForm(formFrom(d)); setNewFiles({}); }} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={save} disabled={saving}>
                {saving ? <Spinner size="sm" /> : "Save changes"}
              </Button>
            </>
          ) : !pending ? (
            <Button variant="outline-secondary" onClick={onClose}>Close</Button>
          ) : mode === "reject" ? (
            <>
              <Button variant="outline-secondary" onClick={() => setMode("view")} disabled={saving}>Back</Button>
              <Button variant="danger" onClick={() => decide("reject")} disabled={saving || !reason.trim()}>
                {saving ? <Spinner size="sm" /> : "Reject"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-danger" onClick={() => setMode("reject")} disabled={saving}>Reject</Button>
              <Button variant="success" onClick={() => decide("approve")} disabled={saving}>
                {saving ? <Spinner size="sm" /> : `Approve ${cfg.label}`}
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/** PAN / Aadhaar KYC requests by tab, with search and submitted-date filters. */
const KycReview = ({ type }) => {
  const cfg = CONFIG[type];
  const [tab, setTab] = useState("pending");
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ type, tab, page, limit });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    const { data: res, error } = await apiCall("GET", `/kyc-review?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load KYC requests.");
    setData(res?.data || null);
  }, [type, tab, page, limit, applied]);

  useEffect(() => {
    load();
  }, [load]);

  // Typing in the search box filters after a short pause
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setTab("pending");
    setPage(1);
    setFilters(EMPTY);
  }, [type]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.values(applied).filter(Boolean).length;
  const summary = data?.summary || { pending: 0, approved: 0, rejected: 0 };
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const pickTab = (k) => {
    setTab(k);
    setPage(1);
  };

  return (
    <div className="plan-settings activation-requests-page kyc-review-page">
      <div className="ps-header">
        <div>
          <h2>{cfg.title}</h2>
          <p>Check the member&apos;s {cfg.label} details and documents, then approve or reject. Rejected members see the reason and can submit again.</p>
        </div>
        <ExportButtons
          title={`${cfg.title} - ${TABS.find((t) => t.key === tab)?.title || ""}`}
          filters={Object.entries(applied).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")}
          columns={[
            { header: "Name", value: (r) => r.name },
            { header: "Username", value: (r) => r.username },
            { header: "Email", value: (r) => r.email },
            { header: "Mobile", value: (r) => r.mobile },
            { header: cfg.numberLabel, value: (r) => cfg.number(r.details) },
            ...(type === "aadhar"
              ? [
                  { header: "Father / husband", value: (r) => (r.details?.fatherName ? `${r.details.fatherName} (${r.details.relation === "husband" ? "husband" : "father"})` : "") },
                  { header: "Address", value: (r) => [r.details?.address, r.details?.pincode].filter(Boolean).join(" - ") },
                ]
              : []),
            { header: "Submitted", value: (r) => when(r.submittedAt) },
            { header: "Status", value: (r) => (r.status === 3 ? "Pending" : r.status === 1 ? "Approved" : "Rejected") },
            { header: "Reviewed", value: (r) => (r.details?.reviewedAt ? when(r.details.reviewedAt) : "") },
            { header: "Reviewed by", value: (r) => r.details?.reviewedBy?.username },
            { header: "Reason", value: (r) => (r.status === 2 ? r.details?.remarks : "") },
          ]}
          getRows={() =>
            collectPages(async (p, l) => {
              const params = new URLSearchParams({ type, tab, page: p, limit: l });
              Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
              const { data: res } = await apiCall("GET", `/kyc-review?${params.toString()}`);
              return { rows: res?.data?.rows || [], pages: res?.data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-stats ar-stats">
        <button type="button" className={`ps-stat ar-stat ar-stat--pending${tab === "pending" ? " ar-stat--active" : ""}`} onClick={() => pickTab("pending")}>
          <span>Pending</span>
          <strong>{summary.pending}</strong>
          <small>Waiting for review</small>
        </button>
        <button type="button" className={`ps-stat ar-stat ar-stat--approved${tab === "approved" ? " ar-stat--active" : ""}`} onClick={() => pickTab("approved")}>
          <span>Approved</span>
          <strong>{summary.approved}</strong>
          <small>{cfg.label} verified</small>
        </button>
        <button type="button" className={`ps-stat ar-stat ar-stat--rejected${tab === "rejected" ? " ar-stat--active" : ""}`} onClick={() => pickTab("rejected")}>
          <span>Rejected</span>
          <strong>{summary.rejected}</strong>
          <small>With a reason sent</small>
        </button>
      </div>

      <Tabs activeKey={tab} onSelect={pickTab} className="ps-tabs" mountOnEnter unmountOnExit>
        {TABS.map((t) => (
          <Tab key={t.key} eventKey={t.key} title={`${t.title} (${summary[t.key]})`}>
            <div className="ps-panel">
              <div className="ar-panel-title">
                <h3>{t.title} requests</h3>
                <span className="ar-count">{total}</span>
                {active > 0 && <span className="ar-filter-badge">{active} filter{active > 1 ? "s" : ""} on</span>}
              </div>

              <div className="ar-filters">
                <Form.Group className="ar-f-search">
                  <Form.Label>Search</Form.Label>
                  <Form.Control size="sm" placeholder={`Username, name, email, mobile or ${cfg.label} number`} value={filters.search} onChange={set("search")} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Submitted from</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.from} max={filters.to || undefined} onChange={set("from")} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Submitted to</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.to} min={filters.from || undefined} onChange={set("to")} />
                </Form.Group>
                <div className="ar-f-reset">
                  <Button size="sm" variant="outline-secondary" disabled={!active} onClick={() => setFilters(EMPTY)}>Reset</Button>
                </div>
              </div>

              <div className="ps-table-wrap">
                <Table className="ps-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Contact</th>
                      <th>{cfg.numberLabel}</th>
                      <th>Submitted</th>
                      {tab !== "pending" && <th>{tab === "approved" ? "Approved" : "Rejected"}</th>}
                      {tab === "rejected" && <th>Reason</th>}
                      <th className="text-end"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={7} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="ps-empty">
                          {active ? "Nothing matches these filters." : tab === "pending" ? `No ${cfg.label} KYC is waiting for review.` : `No ${t.title.toLowerCase()} ${cfg.label} KYC yet.`}
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      rows.map((r) => (
                        <tr key={r.uid}>
                          <td>
                            <div className="ar-member">
                              <span className="ar-avatar">{initials(r)}</span>
                              <div>
                                <div className="ps-strong">{r.name || "—"}</div>
                                <div className="ps-muted ps-small">{r.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="ps-small">
                            <div>{r.email || "—"}</div>
                            <div className="ps-muted">{r.mobile || ""}</div>
                          </td>
                          <td className="kr-number">{cfg.number(r.details) || "—"}</td>
                          <td className="ps-small">{when(r.submittedAt)}</td>
                          {tab !== "pending" && (
                            <td className="ps-small">
                              <div>{when(r.details?.reviewedAt)}</div>
                              {r.details?.reviewedBy?.username && <div className="ps-muted">by {r.details.reviewedBy.username}</div>}
                            </td>
                          )}
                          {tab === "rejected" && <td className="ps-small kr-reason">{r.details?.remarks || "—"}</td>}
                          <td className="text-end">
                            <Button size="sm" className={tab === "pending" ? "ar-primary-btn" : ""} variant={tab === "pending" ? "primary" : "outline-secondary"} onClick={() => setViewing(r)}>
                              {tab === "pending" ? "Review" : "View"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>

              {total > 0 && (
                <div className="ar-pagination">
                  <div className="ar-pagination-info">
                    Showing <strong>{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</strong> of <strong>{total}</strong>
                    <Form.Select size="sm" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="ar-page-size">
                      {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                    </Form.Select>
                  </div>
                  <div className="ar-pages">
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                    <span className="ps-muted ps-small">Page {page} of {pages}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </div>
          </Tab>
        ))}
      </Tabs>

      <ReviewModal type={type} row={viewing} onClose={() => setViewing(null)} onDone={() => { setViewing(null); load(); }} onSaved={load} />
    </div>
  );
};

export default KycReview;
